import {
  FormEvent,
  Fragment,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Tooltip,
  Modal,
  Table,
  Switch,
  Button,
  Spin,
} from 'antd';
import { CalendarOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import UserForm from 'components/user/UserForm';
import { patchUserAsync, patchUserNoteAsync } from 'stores/admin/adminSlice';
import {
  MEETING_DURATION,
  MEETING_DURATION_TIME,
  REQUEST_STATUSES,
  RequestStatus,
} from 'stores/common';
import { useAppDispatch } from 'stores/hooks';
import { User } from 'stores/user/userTypes';
import { Link } from 'react-router-dom';
import { Patient } from 'stores/patients/patientsTypes';
import { SlotDurations, getTotalMinutesSlotsDurations } from 'libs/timeslots';
import dayjs from 'dayjs';
import AdminNoteModal from './AdminNoteModal';

interface Rate {
  requestStatus?: RequestStatus;
  high?: SlotDurations;
  medium?: SlotDurations;
  low?: SlotDurations;
}

interface AdminTableProps {
  currentUser?: User;
  users?: User[];
  loading?: boolean;
  doctorMeetings: Record<string, Patient[]>;
  doctorMeetingsRequestStatuses: Record<string, RequestStatus>;
}
export default function AdminTable({
  currentUser = undefined,
  users = [],
  loading = false,
  doctorMeetings,
  doctorMeetingsRequestStatuses,
} : AdminTableProps) {
  const dispatch = useAppDispatch();
  const [previewMode, setPreviewMode] = useState(false);
  const [previewUser, setPreviewUser] = useState<User>();
  const onOpenPreview = useCallback((user: User) => () => {
    setPreviewUser(user);
    setPreviewMode(true);
  }, [setPreviewMode]);
  const onClosePreview = useCallback(() => setPreviewMode(false), [setPreviewMode]);

  const [noteMode, setNoteMode] = useState(false);
  const [noteUser, setNoteUser] = useState<User>();
  const onOpenNote = useCallback((user: User) => () => {
    setNoteUser(user);
    setNoteMode(true);
  }, [setNoteMode]);
  const onCloseNote = useCallback(() => setNoteMode(false), [setNoteMode]);

  const activateUser = useCallback(
    (user: User) => (activated: boolean, event: FormEvent) => {
      event.stopPropagation();
      dispatch(patchUserAsync({ ...user, activated }));
    },
    [dispatch],
  );
  const promoteUser = useCallback(
    (user: User) => (isStaff: boolean, event: FormEvent) => {
      event.stopPropagation();
      dispatch(patchUserAsync({ ...user, is_staff: isStaff }));
    },
    [dispatch],
  );
  const setUserNote = useCallback(
    (userWPId: string) => (data: any) => {
      setNoteMode(false);
      dispatch(patchUserNoteAsync({ userWPId, note: data.note }));
    },
    [dispatch],
  );
  const userRates = useMemo(
    () => users.reduce((rates: Record<string, Rate>, user) => {
      if (!user.id || !doctorMeetings[user.id]) {
        return rates;
      }

      if (user.id && doctorMeetingsRequestStatuses[user.id] === REQUEST_STATUSES.PENDING) {
        rates[user.id] = { requestStatus: REQUEST_STATUSES.PENDING };
        return rates;
      }

      rates[user.id] = {
        high: getTotalMinutesSlotsDurations(
          user.weekDayList || [],
          user.specialDayList || [],
          user.dayOffList || [],
          doctorMeetings[user.id],
          {
            startDate: dayjs().startOf('day'),
            endDate: dayjs().add(3, 'day').endOf('day'),
          },
        ),
        medium: getTotalMinutesSlotsDurations(
          user.weekDayList || [],
          user.specialDayList || [],
          user.dayOffList || [],
          doctorMeetings[user.id],
          {
            startDate: dayjs().add(4, 'day').startOf('day'),
            endDate: dayjs().add(7, 'day').endOf('day'),
          },
        ),
        low: getTotalMinutesSlotsDurations(
          user.weekDayList || [],
          user.specialDayList || [],
          user.dayOffList || [],
          doctorMeetings[user.id],
          {
            startDate: dayjs().add(8, 'day').startOf('day'),
            endDate: dayjs().add(14, 'day').endOf('day'),
          },
        ),
      };

      return rates;
    }, {}),
    [users, doctorMeetings, doctorMeetingsRequestStatuses],
  );

  const columns: ColumnsType<User> = useMemo(() => [
    {
      key: 'firstName',
      title: 'Nom',
      dataIndex: 'firstName',
      render: (firstName: string, user: User) => (
        <Button
          type='link'
          onClick={onOpenPreview(user)}
        >
          {[firstName, user.lastName].filter((name) => Boolean(name)).join(' ')}
        </Button>
      ),
      width: 120,
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 120,
    },
    {
      key: 'meeting_duration',
      title: 'Durée des RDVs',
      dataIndex: 'meeting_duration',
      width: 120,
      render: (duration: string) => (
        <span>
          {
            duration === MEETING_DURATION.SHORT
              ? MEETING_DURATION_TIME.SHORT
              : MEETING_DURATION_TIME.LONG
          }
        </span>
      ),
    },
    {
      key: 'wp_id',
      title: 'Agenda',
      dataIndex: 'wp_id',
      render: (doctorID: string) => (
        <Link to={`/admin/agenda/${doctorID}`}>
          <Button
            className='!inline-flex !items-center'
            type='link'
            icon={<CalendarOutlined />}
          >
            Voir l&apos;agenda
          </Button>
        </Link>
      ),
      width: 120,
    },
    {
      key: 't3j',
      title: 'Taux à 3j',
      dataIndex: 'id',
      render: (id: string) => {
        const rate = userRates[id];

        if (!rate || !rate.high || rate.requestStatus === REQUEST_STATUSES.PENDING) {
          return <Spin />;
        }

        if (rate.high.availableDuration === 0) {
          return '-';
        }

        return `${((rate.high.meetingDuration / rate.high.availableDuration) * 100).toFixed(0)}%`;
      },
      width: 120,
    },
    {
      key: 't7j',
      title: 'Taux à 7j',
      dataIndex: 'id',
      render: (id: string) => {
        const rate = userRates[id];

        if (!rate || !rate.medium || rate.requestStatus === REQUEST_STATUSES.PENDING) {
          return <Spin />;
        }

        if (rate.medium.availableDuration === 0) {
          return '-';
        }
        const percentage = (rate.medium.meetingDuration / rate.medium.availableDuration) * 100;

        return `${percentage.toFixed(0)}%`;
      },
      width: 120,
    },
    {
      key: 't15j',
      title: 'Taux à 15j',
      dataIndex: 'id',
      render: (id: string) => {
        const rate = userRates[id];

        if (!rate || !rate.low || rate.requestStatus === REQUEST_STATUSES.PENDING) {
          return <Spin />;
        }

        if (rate.low.availableDuration === 0) {
          return '-';
        }

        return `${((rate.low.meetingDuration / rate.low.availableDuration) * 100).toFixed(0)}%`;
      },
      width: 120,
    },
    {
      key: 'outsideMeetings',
      title: 'RDVs hors dispo',
      dataIndex: 'id',
      render: (id: string) => {
        const rate = userRates[id];

        if (!rate || rate.requestStatus === REQUEST_STATUSES.PENDING) {
          return <Spin />;
        }

        return [rate.high, rate.medium, rate.low].reduce((meetings, slotDuration) => {
          if (slotDuration) {
            return meetings + slotDuration.numberOfMeetingOutsideSlots;
          }
          return meetings;
        }, 0);
      },
      width: 120,
    },
    {
      key: 'wp_id',
      title: 'URL Doxy',
      dataIndex: 'wp_id',
      width: 120,
      render: (id: string, user: User) => (
        <Button
          className='!inline-flex !items-center'
          type='link'
          disabled={!user.wp_id || Number.parseInt(user.wp_id, 10) < 0}
          onClick={onOpenNote(user)}
          icon={<EditOutlined />}
        >
          {user.note ? user.note : 'Ajouter un lien doxy'}
        </Button>
      ),
    },
    {
      key: 'activated',
      title: 'Utilisateur\n activé',
      dataIndex: 'activated',
      render: (isActivated: boolean, user: User) => (
        <span>
          <Switch
            disabled={currentUser?.id === user.id || (!user.completed && !isActivated)}
            defaultChecked={isActivated}
            onChange={activateUser(user)}
          />
          &nbsp;
          <Tooltip
            title={user.completed
              ? ''
              : 'Les informations de l\'utilisateur sont incomplètes.'}
          >
            {user.completed ? false : <WarningOutlined />}
          </Tooltip>
        </span>
      ),
      align: 'center',
      width: 24,
    },
    {
      key: 'is_staff',
      title: 'Admin',
      dataIndex: 'is_staff',
      render: (isStaff: boolean, user: User) => (
        <Switch
          disabled={currentUser?.id === user.id}
          defaultChecked={isStaff}
          onChange={promoteUser(user)}
        />
      ),
      align: 'center',
      width: 120,
    },
  ], [onOpenPreview, onOpenNote, currentUser?.id, activateUser, promoteUser, userRates]);

  return (
    <Fragment>
      <Table
        rowKey={(render) => render.id || ''}
        rowClassName='cursor-pointer'
        dataSource={users}
        columns={columns}
        pagination={false}
        loading={loading}
        size='small'
        bordered
      />
      <Modal
        open={previewMode}
        onCancel={onClosePreview}
        footer={null}
        width='768px'
        title='Visualisation des informations'
      >
        {previewMode && (
          <UserForm
            disabled
            user={previewUser}
            key={previewUser?.id}
          />
        )}
      </Modal>
      <AdminNoteModal
        key={noteUser?.wp_id}
        note={noteUser?.note}
        onCancel={onCloseNote}
        onSubmit={setUserNote(noteUser?.wp_id || '')}
        open={noteMode}
      />
    </Fragment>
  );
}
